import { getPreviewText } from '@/components/ui/TextareaWithVars/utils'

import type { CustomVars } from '@/components/ui/TextareaWithVars/interfaces'
import type { LoyaltyReward } from '@/types/types'
import { isNumber } from './utils'

export const getMarketingFlowPreviewText = (
  text: string,
  storeName: string,
  reward?: LoyaltyReward,
  pointValue?: number
) => {
  const variables = getCustomVars({ storeName, reward, pointValue: isNumber(pointValue) ? Number(pointValue) : null })

  return getPreviewText(text, variables)
}

export const getCustomVars = ({
  storeName,
  reward,
  pointValue,
  variablesLength,
  t
}: {
  storeName: string
  reward?: LoyaltyReward
  pointValue?: number
  variablesLength?: Record<string, number>
  t?: (key: string) => string
}) => {
  const variables: CustomVars[] = [
    {
      type: 'variable',
      code: 'store',
      label: t ? t('global.customVars.store.name') : null,
      icon: 'IconoirShop',
      length: storeName.length,
      example: storeName
    },
    {
      type: 'variable',
      code: 'points',
      label: t ? t('global.customVars.points.name') : null,
      icon: 'HeroGiftIconSolid',
      length: pointValue?.toString().length ?? 2,
      example: pointValue?.toString(),
      disabled: !pointValue,
      disabledMessage: t ? t('global.customVars.points.disabledMessage') : null
    },
    {
      type: 'link',
      code: 'reviewLink',
      label: t ? t('global.customVars.reviewLink.name') : null,
      icon: 'HeroGlobeAltIconSolid',
      length: variablesLength?.reviewLink ?? 16,
      example: 'pngo.fr/review'
    },
    {
      type: 'link',
      code: 'customLink',
      label: t ? t('global.customVars.customLink.name') : null,
      icon: 'HeroGlobeAltIconSolid',
      length: variablesLength?.customLink ?? 16,
      example: 'pngo.fr/link',
      customValueType: 'link'
    },
    {
      type: 'webapp',
      code: 'customerApp',
      label: t ? t('global.customVars.customerApp.rewards.name') : null,
      icon: 'HeroGlobeAltIconSolid',
      length: variablesLength?.customerApp ?? 16,
      example: 'pngo.fr/webapp'
    },
    {
      type: 'webapp',
      code: 'customerApp(customer-profile)',
      label: t ? t('global.customVars.customerApp.customerProfile.name') : null,
      icon: 'HeroGlobeAltIconSolid',
      length: variablesLength?.customerApp ?? 16,
      example: 'pngo.fr/webapp'
    },
    {
      type: 'webapp',
      code: 'customerApp(wallet)',
      label: t ? t('global.customVars.customerApp.wallet.name') : null,
      icon: 'HeroGlobeAltIconSolid',
      length: variablesLength?.customerApp ?? 16,
      example: 'pngo.fr/webapp'
    },
    {
      type: 'webapp',
      code: 'customerApp(survey)',
      label: t ? t('global.customVars.customerApp.survey.name') : null,
      icon: 'HeroGlobeAltIconSolid',
      length: variablesLength?.customerApp ?? 16,
      example: 'pngo.fr/webapp',
      customValueType: 'pageId',
      customValueModel: 'survey'
    },
    {
      type: 'variable',
      code: 'reward',
      label: t ? t('global.customVars.reward.name') : null,
      icon: 'HeroGiftIconOutline',
      length: reward?.name?.length ?? 0,
      example: reward?.name,
      disabled: !reward,
      disabledMessage: t ? t('global.customVars.reward.disabledMessage') : null
    },
    {
      type: 'webapp',
      code: 'rewardLink',
      label: t ? t('global.customVars.rewardLink.name') : null,
      icon: 'HeroGiftIconOutline',
      length: variablesLength?.rewardLink ?? 16,
      example: 'pngo.fr/reward',
      disabled: !reward,
      disabledMessage: t ? t('global.customVars.rewardLink.disabledMessage') : null
    },
    {
      type: 'variable',
      code: 'voucher',
      label: t ? t('global.customVars.voucher.name') : null,
      icon: 'HeroReceiptPercentIconSolid',
      length: variablesLength?.voucher ?? 10,
      example: 'XXXXXXXXXX',
      disabled: !reward?.rewardType?.isVoucher,
      disabledMessage: t ? t('global.customVars.voucher.disabledMessageType') : null
    }
  ]

  return variables
}

export const getExternalApiCustomVars = ({
  storeName,
  triggerType,
  t
}: {
  storeName: string
  pointValue?: number
  triggerType?: string | null
  t?: (key: string) => string
}): CustomVars[] => [
  {
    type: 'variable',
    code: 'store.name',
    label: t ? t('global.customVars.store.name') : null,
    icon: 'IconoirShop',
    length: storeName.length,
    example: storeName
  },
  {
    type: 'variable',
    code: 'store.id',
    label: t ? t('global.customVars.storeId.name') : null,
    icon: 'HeroIdentificationIconSolid',
    length: 2,
    example: '42'
  },
  {
    type: 'variable',
    code: 'event_at',
    label: t ? t('global.customVars.eventAt.name') : null,
    icon: 'HeroClockIconSolid',
    length: 20,
    example: '2026-05-07T09:42:00Z'
  },
  {
    type: 'variable',
    code: 'customer.points',
    label: t ? t('global.customVars.totalPoints.name') : null,
    icon: 'HeroGiftIconSolid',
    length: 3,
    example: '120'
  },
  {
    type: 'variable',
    code: 'order.points',
    label: t ? t('global.customVars.orderPoints.name') : null,
    icon: 'HeroGiftIconSolid',
    length: 2,
    example: '15',
    disabled: triggerType !== 'CHECKIN',
    disabledMessage: t ? t('global.customVars.orderPoints.disabledMessage') : null
  },
  {
    type: 'variable',
    code: 'customer.has_optin_sms',
    label: t ? t('global.customVars.has_optin_sms.name') : null,
    icon: 'HeroChatBubbleLeftRightIconSolid',
    length: 4,
    example: 'true'
  },
  {
    type: 'variable',
    code: 'customer.has_optin_email',
    label: t ? t('global.customVars.has_optin_email.name') : null,
    icon: 'HeroEnvelopeIconSolid',
    length: 4,
    example: 'true'
  },
  {
    type: 'variable',
    code: 'customer.phone',
    label: t ? t('global.customVars.customerPhone.name') : null,
    icon: 'HeroPhoneIconSolid',
    length: 12,
    example: '+33612345678'
  },
  {
    type: 'variable',
    code: 'customer.email',
    label: t ? t('global.customVars.customerEmail.name') : null,
    icon: 'HeroEnvelopeIconSolid',
    length: 20,
    example: 'client@exemple.com'
  },
  {
    type: 'variable',
    code: 'customer.id',
    label: t ? t('global.customVars.customerId.name') : null,
    icon: 'HeroIdentificationIconSolid',
    length: 8,
    example: '12345678'
  },
  {
    type: 'variable',
    code: 'customer.first_name',
    label: t ? t('global.customVars.customerFirstName.name') : null,
    icon: 'HeroUserIconSolid',
    length: 6,
    example: 'Camille'
  },
  {
    type: 'variable',
    code: 'customer.last_name',
    label: t ? t('global.customVars.customerLastName.name') : null,
    icon: 'HeroUserIconSolid',
    length: 7,
    example: 'Dupont'
  }
]
